import React, { ReactElement } from 'react';
import { Filter } from '@odin-labs/components';
import { useModalState } from 'utils';
import { smallTablePaginationSizePerPage as limit } from 'utils/constants';
import {
  GetContractorContainerContractorJobsitesDocument,
  useGetContractorContainerContractorJobsitesQuery,
} from 'apollo/generated/client-operations';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { LoadingError } from 'components/loadingError';
import { Pagination } from 'components/pagination';
import {
  EditJobsiteAssignmentModal,
  RemoveJobsiteAssignmentModal,
  RemoveOnboardingLinkModal,
  EditStripePaymentMethodModal,
} from 'containers/contractor/modals';
import { ContractorJobsiteCard } from 'containers/contractor/tabs/ContractorJobsitesTabContainer/contractorJobsiteCard/ContractorJobsiteCard';
import {
  ContractorTabProps,
  JobsiteContractor,
  JobsiteContractorsFilters,
} from 'containers/contractor/types';
import { geJobsiteFilterItems } from 'containers/contractor/helpers/tables';
import {
  OffboardJobsiteContractorModal,
  ReinstateJobsiteContractorModal,
} from 'containers/jobsite/modals';
import { JobsiteContractorModalValue } from './types';

export function ContractorJobsitesTabContainer(
  props: ContractorTabProps,
): ReactElement {
  const { contractorId, userJobsites: jobsites, onTabApiChange } = props;

  const { page, jobsiteIds, search, updateUrl } = usePageQueryParams();
  const offset = page * limit;

  const { data, loading, error, refetch } =
    useGetContractorContainerContractorJobsitesQuery({
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        contractorId,
        includeArchived: true,
        contractorJobsiteContractorsInput: {
          paginationInput: { limit, offset },
          jobsiteIds,
          search,
        },
      },
    });

  const contractor = data?.getContractor;
  const jobsiteContractorsCount = contractor?.jobsiteContractors?.count ?? 0;
  const pageCount = Math.ceil(jobsiteContractorsCount / limit);

  const [jobsiteContractors, setJobsiteContractors] = React.useState<
    JobsiteContractor[]
  >([]);

  React.useEffect(() => {
    setJobsiteContractors(
      contractor?.jobsiteContractors?.edges.map(({ node }) => node) ?? [],
    );
  }, [contractor]);

  const updateJobsiteContractors = React.useCallback(
    (updateValue: Partial<JobsiteContractor>) => {
      setJobsiteContractors((prev) =>
        prev.map((jc) =>
          jc.id === updateValue.id ? { ...jc, ...updateValue } : jc,
        ),
      );
    },
    [setJobsiteContractors],
  );

  const {
    value: jobsiteContractorModalValue,
    isModalOpen,
    openModal,
    closeModal,
    resetModalValue: resetJobsiteContractorModalValue,
  } = useModalState<JobsiteContractorModalValue>(null);
  const { jobsiteContractor, modalAction } = jobsiteContractorModalValue ?? {};

  React.useEffect(() => onTabApiChange({ refetchData: refetch }), [refetch]);

  const filterItems = React.useMemo(
    () => geJobsiteFilterItems({ jobsites, jobsiteIds, search }),
    [jobsites, jobsiteIds, search],
  );

  const onFilterChangeHandler = (
    changedFilters: Partial<JobsiteContractorsFilters>,
  ): void => {
    updateUrl({ page: null, ...changedFilters });
  };

  const onPageChangeHandler = (
    _pageSize: number,
    newPageIndex: number,
  ): void => {
    updateUrl({
      page: newPageIndex ? newPageIndex + 1 : null,
    });
  };

  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <div className="contractor-jobsites-container odin-flex odin-flex-col odin-gap-y-7 odin-pb-7">
      <Filter
        items={filterItems}
        loading={loading}
        firstItemOnRight="search"
        onChange={onFilterChangeHandler}
      />
      {loading ? (
        <ContractorJobsiteCard loading />
      ) : (
        <>
          {jobsiteContractors.map(
            (jc): ReactElement => (
              <ContractorJobsiteCard
                key={`${jc.jobsite.jobsiteId}-jobsite-card`}
                contractor={contractor}
                jobsites={jobsites}
                jobsiteContractor={jc}
                openModal={openModal}
                onJobsiteInvitationCreated={(jobsiteInvitationId) => {
                  updateJobsiteContractors({
                    id: jc.id,
                    jobsiteInvitation: { jobsiteInvitationId },
                  });
                }}
              />
            ),
          )}
          <Pagination
            pageIndex={page}
            pageCount={pageCount}
            pageSize={limit}
            onPageChange={onPageChangeHandler}
          />
        </>
      )}
      {contractor && (
        <>
          <EditJobsiteAssignmentModal
            isOpen={isModalOpen && modalAction === 'edit'}
            closeModal={closeModal}
            onClosed={resetJobsiteContractorModalValue}
            jobsiteContractor={jobsiteContractor}
            contractor={contractor}
            jobsites={jobsites}
          />
          <RemoveJobsiteAssignmentModal
            isOpen={isModalOpen && modalAction === 'delete'}
            closeModal={closeModal}
            onClosed={resetJobsiteContractorModalValue}
            jobsiteContractorId={jobsiteContractor?.id}
          />
          <RemoveOnboardingLinkModal
            isOpen={isModalOpen && modalAction === 'removeOnboardingLink'}
            closeModal={closeModal}
            onClosed={resetJobsiteContractorModalValue}
            onConfirm={(): void => {
              updateJobsiteContractors({
                id: jobsiteContractor?.id,
                jobsiteInvitation: { jobsiteInvitationId: null },
              });
              closeModal();
            }}
            jobsiteInvitationId={
              jobsiteContractor?.jobsiteInvitation?.jobsiteInvitationId
            }
          />
          <EditStripePaymentMethodModal
            isOpen={isModalOpen && modalAction === 'editPaymentMethod'}
            closeModal={closeModal}
            onClosed={resetJobsiteContractorModalValue}
            contractor={contractor}
            jobsiteContractor={jobsiteContractor}
          />
          <OffboardJobsiteContractorModal
            isOpen={isModalOpen && modalAction === 'offboard'}
            closeModal={closeModal}
            onClosed={resetJobsiteContractorModalValue}
            jobsiteContractorId={jobsiteContractor?.id}
            jobsiteName={jobsiteContractor?.jobsite?.name}
            contractorName={contractor?.organization?.name}
            workersCount={jobsiteContractor?.jobsiteWorkers.count ?? 0}
            refetchQueries={[GetContractorContainerContractorJobsitesDocument]}
          />
          <ReinstateJobsiteContractorModal
            isOpen={isModalOpen && modalAction === 'reinstate'}
            closeModal={closeModal}
            onClosed={resetJobsiteContractorModalValue}
            jobsiteContractorId={jobsiteContractor?.id}
            jobsiteName={jobsiteContractor?.jobsite?.name}
            contractorName={contractor?.organization?.name}
            workersCount={jobsiteContractor?.jobsiteWorkers.count ?? 0}
            refetchQueries={[GetContractorContainerContractorJobsitesDocument]}
          />
        </>
      )}
    </div>
  );
}
